import {
  Component, OnInit, OnChanges, OnDestroy,
  Input, SimpleChanges, Output, EventEmitter,
  ViewChild, ElementRef, HostListener
} from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import { GridOptions, RowNode } from 'ag-grid/main';
import {
  NEW_SEARCH_EVENT, NEW_ROW_EVENT, DELETE_ROW_EVENT,
  REFRESH_SEARCH_EVENT, PAGE_OVERFLOW_EVENT, REFRESH_PINNED_ROW_EVENT,
  FINALIZE_STATUS,
  FINALIZE_TABLES
} from '../../search-constants';
import { SearchEvent } from '../../models/search-event';
import { SearchService } from '../../search.service';
import { BasicCellHeaderComponent } from './renderers/basic-cell-header/basic-cell-header.component';
import { BasicCellRendererComponent } from './renderers/basic-cell-renderer/basic-cell-renderer.component';
import { BasicCellEditorComponent } from './editors/basic-cell-editor/basic-cell-editor.component';
import { GridViewKeyEventHandlerService } from '../../../services/grid-view-key-event-handler.service';
import { DataTableEditorService } from '../data-table-editor.service';
import { ButtonItem } from '../../../button/button.model';
import { AlertService,ConfirmDialogService,DqcsEventsService,ResourceService } from 'sce-core';
import { NavigationService } from '../../../../core/navigation.service';
import { PageDefinition } from '../../models/page-definition.model';
import { PaginationEvent } from '../../../events/pagination-event';
import { DataTableConfig } from '../../models/data-table-config';
import { SearchParams } from '../../models/search-params.model';
import { TableRow } from './../../models/table-row.model';
import { TablePreference } from '../../models/table-preference.model';
import { UserPreferenceService } from '../../../../layout/home/services/user-preference.service';
import { PinnedCellRendererComponent } from './renderers/pinned-cell-renderer/pinned-cell-renderer.component';
import { MessageType } from '../../../models/message-type.config';
import { SORTING_FROM_BE_TD } from 'app/constants';

@Component({
  selector: 'lfwms-result-data-table',
  templateUrl: './result-data-table.component.html',
  styleUrls: ['./result-data-table.component.css']
})
export class ResultDataTableComponent implements OnInit, OnChanges, OnDestroy {

  // ag-Grid Element Reference (DOM Access)
  @ViewChild('agGrid', { read: ElementRef }) private agGridRef: ElementRef;
  // MetaData Reference Object
  private metaData: any;
  // MetaData Map : { property => metaData Definition }
  private metaDataMap: any;
  // Id Property to uniquely identify the record
  private savingUserPreference = false;
  private isBackdropSet= false;
  private eventSubscription: Subscription;
  private resizedColumn = '';
  private tableEditor: DataTableEditorService;
  private keyEventHandler: GridViewKeyEventHandlerService;
  private tablePreference: TablePreference;
  private addNewRowOnPageReload = false;
  private isPageDefModified = false;
  private isSortDefModified = false;

  @Input() public popupMode = false;
  @Input() public tableMode = 'V';
  @Input() public searchService: SearchService;
  @Input() public showSelectOption = true;
  @Output() public buttonActionHandler: EventEmitter<string> = new EventEmitter<string>();
  @Output() public onSelectCheckbox: EventEmitter<any> = new EventEmitter<any>();
  @Output() public onPageChange: EventEmitter<any> = new EventEmitter<any>();

  public isGridReady = false;
  public gridHeight = 372;
  /*tableHeightAsNumberOfRows - used inside setBackDrop as well as in initial table setup for setting gridheight */
  public tableHeightAsNumberOfRows = 15;
  public tableConfig: DataTableConfig;
  public gridOptions: GridOptions;
  // Search Params Reference in Search Service
  public searchParams: SearchParams;
  public isSelectedAll = false;
  public selectedRowCount = 0;
  public isLoading = false;
  public columnDefinitions: Array<any>;
  public rowsData: Array<any> = [];
  @Input()
  public displayCustomizer = true;
  public showCustomizer = false;
  public customizerConfig: any;
  public filterModel: any = {};
  public pageDefinition: PageDefinition;
  public pageRows: Array<any>;
  public actionButtons: Array<ButtonItem>;
  public isDataLoadedToGrid = false;
  public isColumnDefinitionLoaded = false;
  public isFullWidthRow = false;
  public targetCell: any;

  /* When the value is 0 ->top paginator and buttons only, value = 1->bottom paginator only,
   anyother value->no paginator and buttons */
  @Input() public showPaginatorAndButtons = 0;
  constructor(
    private readonly resourceService: ResourceService,
    private readonly alertService: AlertService,
    private readonly confirmDialogService: ConfirmDialogService,
    protected navService: NavigationService,
    private readonly userPreferenceService: UserPreferenceService,
    private readonly dqcsEventService: DqcsEventsService
  ) { }

  public ngOnInit() {
    this.initializeTableProperties();
    this.subscribeForSearchEvents();
    // LFWMS- 2954 checking whether row should add
    /*if (this.searchParams.dataTableConfig.enablePinnedRow) {
      this.tableEditor.addPinnedRowToResultTable();
    }*/
    this.gridOptions = this.createGridOptions();
    // LFWM-2730
    this.keyEventHandler.gridOptions = this.gridOptions;
    this.searchService.fetchMetadata().subscribe((_metadata: any) => {
      this.metaData = this.searchService.getMetadata();
      //disable sorting for storer search table as this will be handled in the backend
      if (!SORTING_FROM_BE_TD.includes(this.searchService.searchParams.tableId)) {
        // Sort the columnList based on the index
        this.metaData['columnList'].sort((a: any, b: any) => {
          return ((a.index < b.index) ? -1 : ((a.index > b.index) ? 1 : 0));
        });
      }
      // get the user preference saved for the table
      this.tablePreference = this.searchService.getTablePreference();
      if (this.tablePreference && this.tablePreference.columnConfig && 
        this.tablePreference.columnConfig.configuration && typeof (this.tablePreference.columnConfig.configuration) === 'string') {
        this.tablePreference.columnConfig.configuration = JSON.parse(this.tablePreference.columnConfig.configuration);
      }
      // if the grid is ready and the data is not loaded yet, load it
      this.loadGridIfReady();
    });
    /* Initialize sorting icons if data is already loaded with some sort-order
    currently used in CycleCount when you sort in one tab and switch to another tab.*/
    this.pageDefinition = this.searchService.getPagedData().pageDefinition;
  }

  public ngOnChanges(changes: SimpleChanges) {
    // if the grid is not ready or if the metadata is not loaded, do not proceed.
    if (!this.isGridReady || !this.searchService.getMetadata()) {
      return;
    }
    if (changes['tableMode']) {
      if (['C', 'E', 'V', 'P'].indexOf(this.tableMode) === -1) {
        const errorMsg = 'Invalid Table Mode Input';
        throw new Error(this.getLabel(errorMsg));
      }
      this.loadGridIfReady(true);
      if (this.tableMode !== 'V') {
        if (this.metaDataMap) {
          Object.values(this.metaDataMap)
            .forEach((value: any) => {
              if ((value.displayType === 'EL') || (value.displayType === 'EA')) {
                this.tableEditor.populateRestrictedValues(this.pageRows, value.propertyName);
              }
            });
        }
      }
    }
  }

  // Subscribe for the required events from the SearchService
  private subscribeForSearchEvents() {
    this.eventSubscription = this.searchService.receiveEvent()
      .subscribe(($event: SearchEvent) => {
        switch ($event.type) {
          case NEW_SEARCH_EVENT: {
            if (!this.addNewRowOnPageReload) {
              if (!this.isDataLoadedToGrid) {
                this.loadGridIfReady();
              } else {
                this.loadData();
              }
            } else {
              this.searchService.addNewRowToModel();
              this.addNewRowOnPageReload = false;
            }
            break;
          }
          case NEW_ROW_EVENT: {
            this.loadData();
            break;
          }
          case DELETE_ROW_EVENT: {
            this.loadData();
            break;
          }
          case REFRESH_SEARCH_EVENT: {
            if (this.gridOptions) {
              this.gridOptions.api.refreshCells();
              this.loadData();
            }
            break;
          }
          case REFRESH_PINNED_ROW_EVENT: {
            if (this.gridOptions) {
              this.refreshPinnedRows();
            }
            break;
          }
        }
      });
  }
  private createGridOptions() : GridOptions{
    return {
      onGridReady: (_$event: any) => {
        this.isGridReady = true;
        this.gridOptions.enableFilter = true;
        this.loadGridIfReady();
        this.searchService.getPagedData().aggridApi = this.gridOptions.api;
      },
      onColumnResized: ($event: any) => {
        /* if condition on event.finished is removed because some event capture is lost at times
         * so here the width wil be captured every time when the user resize the column
         * after completing his actions when user tends to navigate to other component
         * the ngDestroy() is called for current component where we save the column width preference
         * call will be give once
         * on refresh or kill of application current state is not save or lost
         */
        this.resizedColumn = $event.column.colId;
        // tablePreference.columnConfig may not be loaded yet (async preference fetch still pending)
        if (!this.tablePreference || !this.tablePreference.columnConfig
          || !this.tablePreference.columnConfig.configuration) {
          return;
        }
        const filteredIndex: number = this.gridOptions.columnApi.getColumnState()
          .findIndex((column: any) => {
            return (column.colId === this.resizedColumn);
          });
        this.tablePreference.columnConfig.configuration.columnWidthsObject[this.resizedColumn]
          = this.gridOptions.columnApi.getColumnState()[filteredIndex].width;
      },
      onColumnPinned: ($event: any) => {
        const columnName = $event.column.colDef.headerName || $event.column.colId
        this.dqcsEventService.publishTableCustomizationEvent({
          table: this.searchService.tableIdforTablePref,
          pinned: $event.pinned ? [columnName] : [],
          unpinned: $event.pinned ? [] : [columnName],
          hidden: [],
          shown: []
        });
      },
      getRowStyle: this.tableConfig.rowStyle ? this.tableConfig.rowStyle : this.getRowStyle,
      context: {
        // Below Reference is to enable parent component's method access for custom cell renderers/editors
        componentParent: this
      },
      overlayLoadingTemplate: '<span class="ag-overlay-loading-center">Please wait while your rows are loading</span>',
      overlayNoRowsTemplate: '<span class="ag-overlay-loading-center">No records to display</span>',
      animateRows: true,
      enableColResize: this.tableConfig.enableColResize,
      singleClickEdit: true,
      suppressClickEdit: this.tableConfig.suppressClickEdit,
      suppressAutoSize: true,
      suppressColumnMoveAnimation: false,
      suppressDragLeaveHidesColumns: true,
      suppressMovableColumns: true,
      suppressFieldDotNotation: false,
      editType: 'fullRow',
      enableFilter: false,
      headerHeight: 60,
      rowHeight: this.tableConfig.rowHeight || 30, // default 40
      isExternalFilterPresent: this.isExternalFilterPresent,
      doesExternalFilterPass: this.doesExternalFilterPass,
      // LFWM-2730
      onRowEditingStarted: (_$event: any) => {
        this.keyEventHandler.editing = true;
      },
      onRowEditingStopped: (_$event: any) => {
        this.keyEventHandler.editing = false;
      },
    };
  }

  private isExternalFilterPresent() {
    return true;
  }

  private doesExternalFilterPass(_node: any) {
    return true;
  }

  public getRowStyle(params: any) {

    if (params.data && params.data['isNewItem'] && params.data['isNewItem'].cVal) {
      return { 'background-color': '#FCBC05', 'color': '#212121' };
    } else {
      if (params.node.rowIndex % 2 === 0) {
        return { 'background-color': '#FFFFFF', 'color': '#212121' };
      } else {
        return { 'background-color': '#E5E5E5', 'color': '#212121' };
      }
    }

  }

  private initializeTableProperties() {
    this.getTableHeightAsPerNumberOfRows();
    
    this.searchParams = this.searchService.searchParams;
    // Initialize table config from search params
    this.tableConfig = this.searchParams.dataTableConfig;
    if (!this.tableConfig) {
      this.tableConfig = new DataTableConfig();
    }
    this.tableEditor = this.searchService.searchParams.tableEditor;
    this.actionButtons = this.searchService.searchParams.actionButtons;
    this.keyEventHandler = new GridViewKeyEventHandlerService(this.tableEditor);
    if (this.tableConfig.keyEventHandler) {
      this.keyEventHandler = this.tableConfig.keyEventHandler;
    } else {
      this.keyEventHandler = new GridViewKeyEventHandlerService(this.tableEditor);
    }
    // Load user preferences
    this.tablePreference = this.searchService.getTablePreference();
    if (this.tablePreference && this.tablePreference.columnConfig) {
      this.loadGridIfReady();
    }
  }

  private getTableHeightAsPerNumberOfRows(){
    //LFWM -2254 IF POPUP NO NEED TO CHECK THE WINDOW SIZE
    if(this.popupMode === true){
      this.tableHeightAsNumberOfRows = 10;
    }
    /*jira 1894
    1920px x 1080px
    1680px x 1050px
    Surface Pro3: 1440px x 960px
    Surface Pro3/4/5: 1368px x 912p
    */
    else if (window.innerHeight >= 1440) {
      this.tableHeightAsNumberOfRows = 40;
      /* 40 row calculation --
          1272X to be hardcoded for larger screens as per jira 1894.
          1272 - 72(header height)  = 1200
          1200/30px(30 is 1 row height) = 40 ~ 40rows.
          Height needed as rows since variable used to setbackdrop also when noofrows are less.
        */
    } else if (window.innerHeight >= 1080) {
      this.tableHeightAsNumberOfRows = 26;
      /* 26 row calculation --
          850PX to be hardcoded for larger screens as per jira 1894.
          850 - 72(header height)  = 778
          778/30px(30 is 1 row height) = 25.9 ~ 26rows.
          Height needed as rows since variable used to setbackdrop also when noofrows are less.
        */
    } else if (window.innerHeight >= 1050) {
      this.tableHeightAsNumberOfRows = 20;
      /* 20 row calculation --
        675PX to be hardcoded for larger screens as per jira 1894.
        675 - 72(header height)  = 603
        603/30px(30 is 1 row height) = 20.1 ~ 20rows.
        Height needed as rows since variable used to setbackdrop also when noofrows are less.
       */
    } else if (window.innerHeight >= 1024) {

      this.tableHeightAsNumberOfRows = 16;
      /* 16 row calculation --
        550PX to be hardcoded for larger screens as per jira 1894.
        550 - 72(header height)  = 468
        468/30px(30 is 1 row height) = 15.6 ~ 16rows.
        Height needed as rows since variable used to setbackdrop also when noofrows are less.
    */
    } else if (window.innerHeight >= 900) {

      this.tableHeightAsNumberOfRows = 15;
      /* 15 row calculation --
        525PX to be hardcoded for larger screens as per jira 1894.
        525 - 72(header height)  = 453
        453/30px(30 is 1 row height) = 15.1 ~ 15rows.
        Height needed as rows since variable used to setbackdrop also when noofrows are less.
    */
    } else {
      this.tableHeightAsNumberOfRows = 10;
    }
    
  }

  private loadGridIfReady(manualRefresh?: boolean) {
    if (this.isGridReady) {
      if ((manualRefresh || !this.isColumnDefinitionLoaded) && this.metaData) {
        this.buildColumnDefinitions();
        this.isColumnDefinitionLoaded = true;
      }
      if ((manualRefresh || !this.isDataLoadedToGrid)
        && this.isColumnDefinitionLoaded && this.searchService.searchStatus.hasResults) {
        this.loadData();
        this.isDataLoadedToGrid = true;
      }
      if (this.tableMode === 'C') {
        this.loadData();
        this.isDataLoadedToGrid = true;
      }
    }
  }

  private buildColumnDefinitions() {
    this.columnDefinitions = [];
    const defaultCellRenderer: any
      = this.tableConfig.defaultCellRenderer ?
        this.tableConfig.defaultCellRenderer : BasicCellRendererComponent;
    const defaultCellEditor: any
      = this.tableConfig.defaultCellEditor ?
        this.tableConfig.defaultCellEditor : BasicCellEditorComponent;
    const defaultHeaderComponent: any
      = this.tableConfig.defaultHeaderComponent ?
        this.tableConfig.defaultHeaderComponent : BasicCellHeaderComponent;
    const defaultPinnedCellRenderer: any
      = this.tableConfig.defaultPinnedCellRenderer ?
        this.tableConfig.defaultPinnedCellRenderer : PinnedCellRendererComponent;
    if (this.tableConfig.indexColumnVisible) {
      this.columnDefinitions.push({
        isRestricted: true,
        headerName: '',
        colId: 'isSelected',
        field: 'isSelected',
        suppressResize: true,
        suppressSizeToFit: true,
        width: this.tableConfig.isSelectedCellWidth,
        pinned: 'left',
        headerComponentFramework: defaultHeaderComponent,
        cellRendererFramework: defaultCellRenderer,
        pinnedRowCellRendererFramework: defaultPinnedCellRenderer,
        index: -1,
        activeIndex: -1,
        suppressFilter: true // Disabling server side filter       
      });
    }
    let columnCount = this.metaData['columnList'].length;
    if (columnCount > 10 && !this.searchParams.isFullFetch
      && (this.searchParams.columnCount > 0)) {
      columnCount = this.searchParams.columnCount;
    }
    this.metaDataMap = {};
    this.metaDataMap['isSelected'] = { displayType: 'R' }; // Display Type :: 'Restricted'
    // Populating ColumnDefinitions for Ag-grid
    this.populateColDefForAggrid(columnCount,defaultCellRenderer,defaultCellEditor,defaultHeaderComponent,defaultPinnedCellRenderer);

    this.applyUserPreferences();
    this.searchService.getPagedData().aggridApi = this.gridOptions.api;
    if (this.gridOptions.api) {
      this.gridOptions.api.setColumnDefs(this.columnDefinitions);
    }
  }

  private populateColDefForAggrid(columnCount: any,defaultCellRenderer: any,defaultCellEditor: any,defaultHeaderComponent:any,defaultPinnedCellRenderer: any){
    for (let x = 0; x < columnCount; x++) {
      const key: string = this.metaData['columnList'][x].propertyName;
      this.metaDataMap[key] = this.metaData['columnList'][x];
      if (this.tableConfig.hiddenPropertiesForTable.indexOf(key) !== -1) {
        continue;
      }
      const columnDefinition: any = new Object();
      // Note:: columnDefinition[isRestricted] = true, will not be available in User Preferences
      columnDefinition.isRestricted = (this.metaDataMap[key].displayType === 'H');
      columnDefinition.headerName = this.resourceService.get(this.metaDataMap[key].elementId);
      columnDefinition.field = columnDefinition.colId = key;
      if (this.metaDataMap[key].type === 'DROPDOWN' || this.metaDataMap[key] === 'EDITABLEDROPDOWN') {
        this.metaDataMap[key].values = JSON.parse(JSON.stringify(this.metaDataMap[key].values));
        /* LFWM-295 Inventory adjustment Screen Changes
     	   Customizing the metadata for display purpose */
        this.customizeMetadataMap(key);
      }
      //LFWM-3353 SCE PROD - PH - Additional Column For Inventory Transaction Module
      // Making the field referenceKey, externReferenceKey, externReferenceType and  remarks
      // hidden in the search filter and visible in the resultant table for Inventory Transcation Module
      if(this.searchParams.tableId === 'invtransactiontd' ){
        this.customizeMetadataMapForHiddenField(key);
        this.customizeColDefForHiddenField(key, columnDefinition);
      }
      //LFWM-3827 Made StartDate and EndDate hidden in searchResponse as these columns are not returned by SP in searchResponse
      if (this.searchParams.tableId === 'wavereleasesearchtd') {
        this.customizeMetadataMapForEditableFields(key);
      }     
      columnDefinition.index = this.metaDataMap[key].index;
      const width: number = this.tableConfig.columnWidth ? this.tableConfig.columnWidth : 250;
      columnDefinition.width = (this.metaDataMap[key].type === 'DATE') ? 267 : width; // Default Column Width
      // minWidth & maxWidth should be set only after setting width property
      const minWidth = this.tableConfig.minColumnWidth ? this.tableConfig.minColumnWidth : 150;
      columnDefinition.minWidth = (this.metaDataMap[key].type === 'DATE') ? 267 : minWidth;
      columnDefinition.maxWidth = 450;
      columnDefinition.headerComponentFramework = (this.tableConfig && this.tableConfig.columnHeaderMapping[key])
        ? this.tableConfig.columnHeaderMapping[key] : defaultHeaderComponent;
      columnDefinition.cellRendererFramework = (this.tableConfig && this.tableConfig.columnRendererMapping[key])
        ? this.tableConfig.columnRendererMapping[key] : defaultCellRenderer;
      columnDefinition.cellRendererParams = { cellMetaData: this.metaDataMap[key] };
      // if this table is not editable, set the editable property as false
      // if the table is editable, set the editable property as a dynamic callback function
      columnDefinition.editable = !this.tableConfig.editable ? false :
        (this.tableConfig.isColumnEditable ? this.tableConfig.isColumnEditable : this.isColumnEditable);
      columnDefinition.cellStyle = (this.tableConfig && this.tableConfig.cellStyle) ? this.tableConfig.cellStyle : { 'overflow': 'visible' };
      columnDefinition.autoHeight = (this.tableConfig && this.tableConfig.autoHeight) ? this.tableConfig.autoHeight : false;
      columnDefinition.wrapText = (this.tableConfig && this.tableConfig.wrapText) ? this.tableConfig.wrapText : false;
      columnDefinition.cellEditorFramework = (this.tableConfig && this.tableConfig.columnEditorMapping[key])
        ? this.tableConfig.columnEditorMapping[key] : defaultCellEditor;
      columnDefinition.cellEditorParams = { cellMetaData: this.metaDataMap[key] };
      columnDefinition.pinnedRowCellRendererFramework = (this.tableConfig && this.tableConfig.columnPinnedRendererMapping[key])
        ? this.tableConfig.columnPinnedRendererMapping[key] : defaultPinnedCellRenderer;
      columnDefinition.suppressKeyboardEvent = (params: any) => {
        // Note :: While Editing Navigation via Arrow Keys will be disabled
        const KEY_LEFT = 37;
        const KEY_UP = 38;
        const KEY_RIGHT = 39;
        const KEY_DOWN = 40;
        const KEY_PAGE_UP = 33;
        const KEY_PAGE_DOWN = 34;
        const KEY_PAGE_HOME = 36;
        const KEY_PAGE_END = 35;
        return (params.editing && ((params.event.keyCode === KEY_LEFT)
          || (params.event.keyCode === KEY_UP)
          || (params.event.keyCode === KEY_RIGHT)
          || (params.event.keyCode === KEY_DOWN)
          || (params.event.keyCode === KEY_PAGE_UP)
          || (params.event.keyCode === KEY_PAGE_DOWN)
          || (params.event.keyCode === KEY_PAGE_HOME)
          || (params.event.keyCode === KEY_PAGE_END)));
      };
      this.columnDefinitions.push(columnDefinition);
    }
  }

  /**
  * If the table is in View Mode make all columns as non editable
  * In the Edit Mode, make columns editable based on its metadata
  * In Create Mode, make all columns as editable
  * @paramdisplayType : The column displayType as in metadata
  */
  private isColumnEditable(params: any) {
    const rowData = params.data;
    const componentRef = params.context.componentParent;
    // if the table is in view mode, do not make any column as editable
    if (['V', 'P'].indexOf(componentRef.tableMode) !== -1) {
      return false;
    }

    const isNewRow: boolean = rowData && rowData['isNewItem'] && rowData['isNewItem'].cVal;
    // if this is a new row and if its id is not auto generated, then enable all columns
    if (isNewRow && !componentRef.searchParams.autoGenerateIdProperty) {
      return true;
    } else if (rowData && rowData.externasnStatus && rowData.externasnStatus.cValOrig === '9') {
      return false;
    } else if (rowData && rowData.containerKey && rowData.status && rowData.status.cValOrig === '9') {
      return false;
    } 
    // restricting users to edit shipped MBOLs and finalized KITs
    else if(rowData && rowData.status && rowData.status.cValOrig === FINALIZE_STATUS && FINALIZE_TABLES.includes(componentRef.searchParams.tableId)){
      return false;
    } else if(rowData && rowData.status && rowData.status.cValOrig === MessageType.msgCancel  && componentRef.metaData.moduleId === 'sotd'){
      return false;
    } else {
      // enable the column only if it is an editable field in the metadata
      return (['E', 'EL', 'L', 'EA', 'ET'].indexOf(componentRef.metaDataMap[params.colDef.colId].displayType) > -1);
    }
  }

  // Method to apply/refresh user preferences
  private applyUserPreferences() {
    if (this.tableConfig.tablePreferencDisable) {
      return;
    }
    // tablePreference.columnConfig may not be loaded yet (async preference fetch still pending)
    if (!this.tablePreference || !this.tablePreference.columnConfig
      || !this.tablePreference.columnConfig.configuration) {
      return;
    }
    this.columnDefinitions
      .forEach((columnDefinition: any) => {
        const key = columnDefinition.colId;
        const config = this.tablePreference.columnConfig.configuration;
        columnDefinition.hide =
          ((config.hiddenPropertiesList.indexOf(key) > -1)
            || (this.metaDataMap[key].displayType === 'H'));
        columnDefinition.activeIndex =
          (config.propertyIndexMap && config.propertyIndexMap[key]) ?
          config.propertyIndexMap[key] :
          columnDefinition.index;
        if (!columnDefinition.isRestricted) {
          columnDefinition.pinned =
            (config.pinnedPropertiesList.indexOf(key) > -1) ? 'left' : null;
          if (config.columnWidthsObject && config.columnWidthsObject[key]) {
            columnDefinition.width = config.columnWidthsObject[key];
          } else {
            const width: number = this.tableConfig.columnWidth ? this.tableConfig.columnWidth : 250;
            columnDefinition.width = (this.metaDataMap[key].type === 'DATE') ? 267 : width;
            /**
             * The below code is added here from line no: 352
             * This added because if a column has special width then that has to be applied over default width
             * but this width should not override the user preference width for the same column
             * you can refer wave release 'editicon' column for implements special width feature
             */
            const propertyName = this.metaDataMap[key].propertyName;
            const customColumnWidth = this.tableConfig.specialWidthDetails[propertyName];
            if (customColumnWidth) {
              columnDefinition.minWidth = customColumnWidth;
              columnDefinition.width = customColumnWidth;
            }
          }
        }
      });
      if (!SORTING_FROM_BE_TD.includes(this.searchService.searchParams.tableId)) {
        this.columnDefinitions
        .sort((a: any, b: any) => {
          return ((a.activeIndex < b.activeIndex) ? -1 : ((a.activeIndex > b.activeIndex) ? 1 : 0));
        });
      }
  }

  // Method to Customize Metadata for Display Only Changes
  private customizeMetadataMap(key: any) {
    // Overriding Metadata for finalized flag to modify the display type while in Create mode
    // Below, Deep Clone has to be performed to avoid directly updating Metadata Object in Service
    /*LFWM-295 adding this check as the finalized Flag dropdown should be
        disabled while creating new adjustment detail*/
    if (this.tableMode === 'C') {
      if (this.searchParams.tableId
        && this.searchParams.tableId === 'adjdetailtd' && key === 'finalizedflag') {
        const finalizedFlagMetadata = JSON.parse(JSON.stringify(this.metaDataMap[key]));
        finalizedFlagMetadata.displayType = 'V';
        this.metaDataMap[key] = finalizedFlagMetadata;
      }
    }
  }
  // Method to Customize Metadata for Display Only Changes
  private customizeMetadataMapForHiddenField(key: any) {
    // Overriding Metadata for the filed referenceKey, externReferenceKey, externReferenceType and remarks
    // The above fields are hidden in the search filter and make display type V in the table
    // Below, Deep Clone has to be performed to avoid directly updating Metadata Object in Service
    //LFWM-3353 SCE PROD - PH - Additional Column For Inventory Transaction Module
      if (this.searchParams.tableId
        && this.searchParams.tableId === 'invtransactiontd' 
        && (key === 'referenceKey'||
            key === 'externReferenceKey'||
            key === 'remarks'||
            key === 'externReferenceType'
            )) {
        const inventoryTransactionMetadata = JSON.parse(JSON.stringify(this.metaDataMap[key]));
        inventoryTransactionMetadata.displayType = 'V';
        this.metaDataMap[key] = inventoryTransactionMetadata;
      }
  }
  // LFWM - 3784 columnDefinition[isRestricted] made false to show in user preference
  private customizeColDefForHiddenField(key: any, colDef: any) {
    if (this.searchParams.tableId
      && this.searchParams.tableId === 'invtransactiontd'
      && (key === 'referenceKey' ||
        key === 'externReferenceKey' ||
        key === 'remarks' ||
        key === 'externReferenceType'
      )) {
      colDef.isRestricted = false;
    }

  }

  private loadData() {
    if (!this.columnDefinitions) {
      // column definitions are not built yet. Skip this event. Data will be loaded once columns are built
      return;
    }
    if (this.tablePreference && this.tablePreference.columnConfig
      && typeof (this.tablePreference.columnConfig.configuration) === 'string') {
      this.tablePreference.columnConfig.configuration = JSON.parse(this.tablePreference.columnConfig.configuration);
    }
    if ((this.isPageDefModified || this.isSortDefModified)
      && this.tablePreference && this.tablePreference.columnConfig && this.tablePreference.columnConfig.configuration) {
      if (this.tablePreference.columnConfig.configuration.pageDefinitionObject) {
        this.tablePreference.columnConfig.configuration.pageDefinitionObject = {};
      }
      this.tablePreference.columnConfig.configuration
        .pageDefinitionObject['pageSize'] = this.pageDefinition.pageSize;
      this.tablePreference.columnConfig.configuration
        .pageDefinitionObject['sortColumn'] = this.pageDefinition.sortColumn;
      this.tablePreference.columnConfig.configuration
        .pageDefinitionObject['sortOrder'] = this.pageDefinition.sortOrder;
      this.saveUserPreferences();
    }
    this.pageRows = this.searchService.getPagedData().pageRows;
    this.gridHeight = this.tableConfig.gridHeight ? this.tableConfig.gridHeight : (72 + (this.gridOptions.rowHeight * this.tableHeightAsNumberOfRows));
    this.rowsData = [];
    this.selectedRowCount
      = (this.showSelectOption && !this.searchParams.isSingleSelect) ? 0 : -1;
    this.getPgData();
    this.setLoader(false);
    if (this.targetCell) {
      if (this.tableMode === 'V') {
        this.gridOptions.api
          .setFocusedCell(this.targetCell.rowIndex, this.targetCell.colKey);
      } else {
        this.gridOptions.api.startEditingCell(this.targetCell);
      }
      this.targetCell = null;
    }
  }

  private getPgData(){
    this.pageRows.forEach((pageRow: TableRow) => {
      if (this.selectedRowCount !== -1 && pageRow.isSelected && !pageRow.isNewRow) {
        this.selectedRowCount++;
      }
      const rowDefinition: any = { isSelected: pageRow.isSelected };
      if(pageRow.cells) {
        Object.keys(pageRow.cells)
        .forEach((key: any) => {
          rowDefinition[key] = pageRow.cells[key];
        });
      }
      if (pageRow['showAdditionalSelectionIcon']) {
        rowDefinition['showAdditionalSelectionIcon'] = true;
      }
      this.rowsData.push(rowDefinition);
    });
    this.isSelectedAll = ((this.pageRows.length > 0)
    && (this.selectedRowCount === this.pageRows.length));
    this.gridOptions.api.setRowData(this.rowsData);
    if (this.searchService.getPagedData().pinnedPageRows.length > 0) {
      this.gridHeight += (this.searchService.getPagedData().pinnedPageRows.length * 30);
      this.gridOptions.api.setPinnedBottomRowData(this.searchService.getPagedData().pinnedPageRows);
    }
    if ((this.tableMode !== 'V') && this.pageRows.length > 0) {
      this.gridOptions.api.setFocusedCell(0, 'isSelected');
      if (this.tableConfig.autoTriggerEdit) {
        this.gridOptions.api.startEditingCell({ rowIndex: 0, colKey: 'isSelected' });
      }
    }
  }

  private setLoader(isLoading: boolean) {
    if (isLoading) {
      this.isLoading = isLoading;
      this.gridOptions.api.showLoadingOverlay();
    } else {
      const gridWidth: number
        = this.agGridRef.nativeElement.querySelector('.ag-body').clientWidth;
      const totalColumnWidth: number
        = this.agGridRef.nativeElement
          .querySelector('.ag-body-container').clientWidth
        + this.agGridRef.nativeElement
          .querySelector('.ag-pinned-left-cols-container').clientWidth;
      this.isFullWidthRow = (totalColumnWidth < gridWidth);
      if (!this.isBackdropSet) {
        this.setBackdrop();
      }
      if (this.rowsData.length === 0) {
        this.gridOptions.api.showNoRowsOverlay();
      } else {
        this.gridOptions.api.hideOverlay();
      }
      this.isLoading = isLoading;
    }
  }

  private setBackdrop() {
    let boxShadowCSS= '';
    const rowHeight: number = this.gridOptions.rowHeight - 1;
    let shadowHeight = 0;
    const rowsToDummyFillBg = this.tableHeightAsNumberOfRows > this.pageDefinition.pageSize ? this.tableHeightAsNumberOfRows : this.pageDefinition.pageSize;
    for (let i = 1; i <= rowsToDummyFillBg; i++) {
      if (i !== 1) {
        boxShadowCSS += ', ';
      }
      shadowHeight += rowHeight;
      boxShadowCSS += `inset 0px  ${shadowHeight} px 0px 0px #  ((i % 2 === 0) ? 'E5E5E5' : 'FFFFFF')`;
      shadowHeight++;
      boxShadowCSS += `, inset 0px ${shadowHeight} px 0px 0px #D9DCDE`;
    }
    this.agGridRef.nativeElement.querySelector('.ag-body').style.boxShadow = boxShadowCSS;
    this.isBackdropSet = true;
  }

  public toggleSelectAll(selectAll?: boolean) {
    if (this.selectedRowCount !== -1) {
      // Note: Select All will work only when atleast 1 saved row is present
      const savedRecord: TableRow = this.pageRows
        .find((pageRow: TableRow) => {
          return !pageRow.isNewRow;
        });
      if (!savedRecord) {
        return;
      }
      this.gridOptions.api.stopEditing();
      if (selectAll === undefined) {
        this.isSelectedAll = !this.isSelectedAll;
      } else {
        this.isSelectedAll = selectAll;
      }
      this.pageRows
        .forEach((pageRow: TableRow, index: number) => {
          if (!pageRow.isNewRow && !pageRow.isSelectionFreezed) {
            pageRow.isSelected = this.rowsData[index].isSelected = this.isSelectedAll;
          }
        });
      this.selectedRowCount = this.isSelectedAll ? this.pageRows.length : 0;
      this.gridOptions.api.refreshCells({ columns: ['isSelected'] });
      // event is emitted on enabling or disabling the checkbox at header level
      this.onSelectCheckbox.emit();
    }
  }

  public toggleSelect(rowIndex: number, rowNode: RowNode) {
    if ((this.selectedRowCount !== -1 && !this.pageRows[rowIndex].isSelectionFreezed)
      || (this.showSelectOption && this.searchParams.isSingleSelect)) {
      this.rowsData[rowIndex].isSelected
        = this.pageRows[rowIndex].isSelected = !this.rowsData[rowIndex].isSelected;
      if (this.selectedRowCount !== -1 && !this.pageRows[rowIndex].isSelectionFreezed) {
        this.rowsData[rowIndex].isSelected ? this.selectedRowCount++ : this.selectedRowCount--;
        this.isSelectedAll = (this.selectedRowCount === this.rowsData.length);
        this.gridOptions.api.refreshCells({ columns: ['isSelected'], rowNodes: [rowNode] });
      }
      this.getShowSelectedOption(rowIndex,rowNode);
      this.gridOptions.api.stopEditing();
      // event is emitted on enabling or disabling the checkbox at row level
      this.onSelectCheckbox.emit(rowIndex);
    }
  }

  private getShowSelectedOption(rowIndex: number, rowNode: RowNode){
    if (this.showSelectOption && this.searchParams.isSingleSelect) {
      if (this.rowsData[rowIndex].isSelected) {
        this.rowsData.forEach((rowData: TableRow, index: number) => {
            rowData.isSelected = this.pageRows[index].isSelected = (index === rowIndex);
          });
        this.gridOptions.api.refreshCells({ columns: ['isSelected'] });
      } else {
        this.gridOptions.api.refreshCells({ columns: ['isSelected'], rowNodes: [rowNode] });
      }
    }
  }

  public toggleColumnSort(colId: string) {
    if (colId === this.pageDefinition.sortColumn) {
      this.pageDefinition.sortOrder
        = (this.pageDefinition.sortOrder === 1) ? 0 : 1;
    } else {
      this.pageDefinition.sortColumn = colId;
      this.pageDefinition.sortOrder = 1;
    }
    this.isSortDefModified = true;
    this.setLoader(true);
    this.searchService.performSearch();
  }

  public onPageChanged(paginationEvent: PaginationEvent) {
    if (!this.searchParams.paginateLocally && this.isDetailsEdited()) {
      // if there are any edited cells, show a confirmation dialog
      this.confirmDialogService.confirmDialog(this.resourceService.get('msg_save_rcrd'))
        .subscribe((isConfirm: boolean) => {
          if (isConfirm) {
            this.toggleSelectAll(false);
            const event: SearchEvent = new SearchEvent(PAGE_OVERFLOW_EVENT);
            this.searchService.publishEvent(event);
            return;
          }
        });
    } else {
      this.pageDefinition.pageNumber = paginationEvent.pageNumber;
      this.isPageDefModified = (this.pageDefinition.pageSize !== paginationEvent.pageSize);
      this.pageDefinition.pageSize = paginationEvent.pageSize;
      this.isBackdropSet = (this.pageDefinition.pageSize !== paginationEvent.pageSize);
      this.setLoader(true);
      this.searchService.setIsFromPageChange(true);
      this.searchService.performSearch();
    }
    this.onPageChange.emit();
  }

  public getLabel(key: any) {
    return this.resourceService.get(key);
  }

  public openLookup(row: any, property: string) {
    this.tableEditor.openLookup(row, property);
  }

  public addNewRow() {
    if (this.pageRows.length < this.pageDefinition.pageSize) {
      this.searchService.addNewRowToModel();
    } else {
      if (!this.searchParams.paginateLocally && this.isDetailsEdited()) {
        // if there are any edited cells, show a confirmation dialog
        this.confirmDialogService.confirmDialog(this.resourceService.get('msg_save_rcrd'))
          .subscribe((isConfirm: boolean) => {
            if (isConfirm) {
              this.toggleSelectAll(false);
              const event: SearchEvent = new SearchEvent(PAGE_OVERFLOW_EVENT);
              this.searchService.publishEvent(event);
              return;
            }
          });
      } else {
        // if nothing is edited, navigate to the last page and add a new row
        this.navigateToLastPageAndAddRow();
      }
    }
  }

  // Function to delete newly added row before saving
  public deleteNewRow(rowIndexToDelete: number) {
    this.searchService.deleteNewRowFromModel(rowIndexToDelete);
  }

  private isDetailsEdited() {
    return this.pageRows
      .findIndex((row: any) => {
        return row.isEdited;
      }) >= 0;
  }

  private navigateToLastPageAndAddRow() {
    if (this.pageDefinition.recordCount >= this.pageDefinition.pageSize) {
      let lastPage = Math.ceil(this.pageDefinition.recordCount / this.pageDefinition.pageSize);
      const rowCountOnLastPage = this.pageDefinition.recordCount % this.pageDefinition.pageSize;
      if (rowCountOnLastPage === 0) {
        // if the last page is full, go to the next page
        lastPage++;
      }
      if (lastPage !== this.pageDefinition.pageNumber) {
        const pageEvent = new PaginationEvent(PaginationEvent.PAGE_CHANGE);
        pageEvent.pageNumber = lastPage;
        pageEvent.pageSize = this.pageDefinition.pageSize;
        this.onPageChanged(pageEvent);
        this.addNewRowOnPageReload = true;
      } else {
        this.searchService.addNewRowToModel();
      }
    }
  }

  public handleButtonPanelActions(action) {
    if (action === 'Add Row') {
      if (this.searchParams.tableId === 'skuconfigtd' || this.searchParams.tableId === 'upctd') {
        /* [LFWM-2923] - to check whether the storer is empty if the page conatins storerKey field in accordion
              before adding new row to table - enabling only for commodity screen tables now */
        if (this.tableEditor.checkForStorer()) {
          this.addNewRow();
        } else {
          this.alertService.clearAll().error(this.resourceService.get('msg_storer_not_provided'));
          return;
        }
      } else {
        this.addNewRow();
      }
    } else {
      this.buttonActionHandler.emit(action);
    }
  }

  public ngOnDestroy() {
    /* call to save user column width preference */
    this.saveUserPreferences();
    if (this.eventSubscription) {
      this.eventSubscription.unsubscribe();
    }
  }

  public fetchSearchResults(key: string, value: string, displayType: string) {
    this.filterModel[key] = value;
    this.tableEditor.getAutoFilter(key, value, this.searchService, displayType);
  }

  public showCustomizerPanel() {
    if (!this.isLoading) {
      // tablePreference.columnConfig may not be loaded yet (async preference fetch still pending)
      if (!this.tablePreference || !this.tablePreference.columnConfig) {
        return;
      }
      this.customizerConfig = {};
      this.customizerConfig.columnDefinitions = this.columnDefinitions;
      this.customizerConfig.pageDefinitionObjectBackup
        = Object.assign({}, this.tablePreference.columnConfig.configuration.pageDefinitionObject);
      this.customizerConfig.idProperty = this.searchParams.idProperty;
      this.customizerConfig.showIdPropertyAsHyperLink = this.tableConfig.showIdAsAHyperLink;
      this.customizerConfig.userPreferences = this.tablePreference.columnConfig;
      this.showCustomizer = true;
    }
  }

  public onCustomized() {
    this.showCustomizer = false;
    if (this.tablePreference && this.tablePreference.columnConfig && this.tablePreference.columnConfig.configuration) {
      this.applyUserPreferences();
      if (this.gridOptions.api) {
        this.gridOptions.api.setColumnDefs(this.columnDefinitions);
      }
      this.loadData();
      if ((this.tablePreference.columnConfig.configuration
        .pageDefinitionObject['pageSize'] !== this.pageDefinition.pageSize)
        || (this.tablePreference.columnConfig.configuration
          .pageDefinitionObject['sortColumn'] !== this.pageDefinition.sortColumn)
        || (this.tablePreference.columnConfig.configuration
          .pageDefinitionObject['sortOrder'] !== this.pageDefinition.sortOrder)) {
        this.pageDefinition.pageSize = this.tablePreference
          .columnConfig.configuration.pageDefinitionObject['pageSize'];
        this.pageDefinition.sortColumn = this.tablePreference
          .columnConfig.configuration.pageDefinitionObject['sortColumn'];
        this.pageDefinition.sortOrder = this.tablePreference
          .columnConfig.configuration.pageDefinitionObject['sortOrder'];
        this.searchService.performSearch();
      }
    }
  }

  public toggleColumnPinning(colId: string, pinned: string) {
    if (this.savingUserPreference) {
      return;
    }
    const filteredIndex: number = this.columnDefinitions
      .findIndex((column: any) => {
        return (column.colId === colId);
      });
    this.columnDefinitions[filteredIndex].pinned = (pinned ? null : 'left');
    this.gridOptions.columnApi.setColumnPinned(colId, this.columnDefinitions[filteredIndex].pinned);
    // tablePreference.columnConfig may not be loaded yet (async preference fetch still pending)
    if (!this.tablePreference || !this.tablePreference.columnConfig
      || !this.tablePreference.columnConfig.configuration) {
      return;
    }
    if (this.columnDefinitions[filteredIndex].pinned) {
      this.tablePreference.columnConfig.configuration.pinnedPropertiesList.push(colId);
    } else {
      this.tablePreference.columnConfig.configuration.pinnedPropertiesList
        .splice(this.tablePreference.columnConfig.configuration.pinnedPropertiesList.indexOf(colId), 1);
    }
    this.saveUserPreferences();
  }

  private saveUserPreferences() {
    // tablePreference.columnConfig may not be loaded yet if the component is destroyed
    // before the async preference fetch resolves (e.g. quick navigation away from the page)
    if (!this.tablePreference || !this.tablePreference.columnConfig) {
      return;
    }
    this.savingUserPreference = true;
    this.userPreferenceService.saveTablePreferences(this.tablePreference.columnConfig)
      .subscribe((data: any) => {
        this.savingUserPreference = false;
        if (!this.tablePreference.columnConfig.preferenceId) {
          this.tablePreference.columnConfig.preferenceId = data.preferenceId;
        }
        this.isPageDefModified = false;
        this.isSortDefModified = false;
      }, (error: any) => {
        this.savingUserPreference = false;
        this.alertService.clearAll().error(this.resourceService.get('msg_pref_sav_fail'));
      });
  }

  //LFWM-2954 - Refreshing grid bottom rows.
  private refreshPinnedRows(){
    if(this.searchService.getPagedData().pinnedPageRows){
      this.searchService.getPagedData().pinnedPageRows.forEach((row,i)=>{
        this.gridOptions.api.getPinnedBottomRow(i).setData(row);
      });
    }
  }

  // Method to Customize Metadata for changing the Display Type
  private customizeMetadataMapForEditableFields(key: any) {
    // Overriding Metadata for the filed - StartDate and EndDate
    // The above fields are editable in the search filter and needs tobe hidden in searchResponse
    // Below, Deep Clone has to be performed to avoid directly updating Metadata Object in Service
      if (this.searchParams.tableId
        && this.searchParams.tableId === 'wavereleasesearchtd' 
        && (key === 'fromDate'||
            key === 'toDate'
            )) {
        const inventoryTransactionMetadata = JSON.parse(JSON.stringify(this.metaDataMap[key]));
        inventoryTransactionMetadata.displayType = 'H';
        this.metaDataMap[key] = inventoryTransactionMetadata;
      }
  }
}



// WEBPACK FOOTER //
// /home/runner/work/fbm-fulfillment-wms-client/fbm-fulfillment-wms-client/src/app/shared/search/results/result-data-table/result-data-table.component.ts